// File: src/contexts/ThemeContext.js
import { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Estado inicial: localStorage ou preferência do sistema
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem('app_theme');
    if (savedTheme) return savedTheme;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };

  const [theme, setTheme] = useState(getInitialTheme());
  const isDarkMode = theme === 'dark';

  // Carregar tema do Firebase ONCE quando usuário fizer login
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const docSnapshot = await getDoc(userDocRef);
          if (docSnapshot.exists()) {
            const userData = docSnapshot.data();
            const fbTheme = userData.theme || userData.adminTheme;
            
            setTheme(currentTheme => {
              if (fbTheme && fbTheme !== currentTheme) {
                localStorage.setItem('app_theme', fbTheme);
                return fbTheme;
              }
              return currentTheme;
            });
          }
        } catch (error) {
          console.error('Erro ao carregar tema do Firebase:', error);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  // Sincronizar tema entre abas usando localStorage (mais seguro que onSnapshot)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'app_theme' && (e.newValue === 'dark' || e.newValue === 'light')) {
        setTheme(e.newValue);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Atualizar DOM e LocalStorage quando o tema mudar
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('app_theme', theme);
  }, [isDarkMode, theme]);

  // Função para alternar o tema (salva no banco)
  const toggleTheme = async () => {
    const newTheme = isDarkMode ? 'light' : 'dark';
    
    // Atualiza estado local imediatamente (otimista)
    setTheme(newTheme);
    localStorage.setItem('app_theme', newTheme);

    const user = auth.currentUser;
    // Persiste no Firebase
    if (user) {
      try {
        await updateDoc(doc(db, 'users', user.uid), {
          theme: newTheme,
          adminTheme: newTheme, // Sincroniza ambos para evitar conflitos legados
          themeUpdatedAt: new Date()
        });
        console.log(`✅ Tema global salvo: ${newTheme}`);
      } catch (error) {
        console.error('Erro ao salvar tema no Firebase:', error);
      }
    }
  };

  const value = {
    isDarkMode,
    toggleTheme,
    theme,
    // Expostos por retrocompatibilidade
    dashboardTheme: theme,
    adminTheme: theme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
